import { useState } from "react";
import { motion } from "framer-motion";
import { Send, GraduationCap, Mail, Phone, User, Building2, Calendar, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const TRACKS = [
  "Software Engineering",
  "AI / Machine Learning",
  "GovTech / PMO",
  "Cloud & DevOps",
  "Data Analytics",
  "UI/UX Design",
  "Cybersecurity",
];
const DURATIONS = ["2 months", "3 months", "6 months"];

const EMPTY = {
  name: "", email: "", phone: "", institution: "", course: "",
  year: "", track: TRACKS[0], duration: DURATIONS[0], cover_letter: "",
};

export default function InternshipForm() {
  const [data, setData] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.phone || !data.institution) {
      toast.error("Name, email, phone and institution are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/internships/apply", data);
      toast.success("Application received! Our team will reach out shortly.");
      setData(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="internship" data-testid="section-internship" className="relative bg-white py-24 md:py-32 overflow-hidden">
      <div className="absolute -top-32 -left-32 h-72 w-72 bg-blue-200/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 h-72 w-72 bg-[#FF9933]/20 rounded-full blur-3xl" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="section-overline" style={{ color: "#FF9933" }}>Internships</div>
          <h2 className="font-display text-4xl md:text-5xl font-semibold mt-4 leading-tight text-balance text-[#0F172A]">
            Apply for an Internship at GESIL.
          </h2>
          <p className="mt-4 text-[#475569] text-base md:text-lg">
            Work on India&apos;s most ambitious GovTech, AI and citizen-service platforms. Real ownership from day one.
          </p>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          data-testid="internship-form"
          className="bg-white border border-blue-100 rounded-3xl p-6 md:p-10 shadow-[0_30px_80px_-30px_rgba(37,99,235,0.25)]"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Input icon={User} label="Full Name" value={data.name} onChange={(v) => set("name", v)} testid="intern-name" required />
            <Input icon={Mail} label="Email" type="email" value={data.email} onChange={(v) => set("email", v)} testid="intern-email" required />
            <Input icon={Phone} label="Phone" value={data.phone} onChange={(v) => set("phone", v)} testid="intern-phone" required />
            <Input icon={Building2} label="Institution / University" value={data.institution} onChange={(v) => set("institution", v)} testid="intern-institution" required />
            <Input icon={GraduationCap} label="Course / Branch" value={data.course} onChange={(v) => set("course", v)} testid="intern-course" />
            <Input icon={Calendar} label="Year of Study" value={data.year} onChange={(v) => set("year", v)} placeholder="e.g. 3rd Year" testid="intern-year" />
            <Select icon={Briefcase} label="Preferred Track" value={data.track} onChange={(v) => set("track", v)} options={TRACKS} testid="intern-track" />
            <Select icon={Calendar} label="Duration" value={data.duration} onChange={(v) => set("duration", v)} options={DURATIONS} testid="intern-duration" />
          </div>
          <div className="mt-5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#0F172A] mb-2">
              Cover letter (optional)
            </label>
            <textarea
              value={data.cover_letter}
              onChange={(e) => set("cover_letter", e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Why are you excited to intern at GESIL? Share any relevant projects or skills."
              data-testid="intern-cover-letter"
              className="w-full px-4 py-3 rounded-xl border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm resize-y text-[#0F172A]"
            />
          </div>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#475569]">
              By submitting, you agree to be contacted by GESIL regarding your application.
            </p>
            <button
              type="submit"
              disabled={sending}
              data-testid="intern-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-60 text-white px-7 py-3.5 text-sm font-semibold tracking-wide uppercase transition-colors shadow-[0_18px_40px_-18px_rgba(59,130,246,0.7)]"
            >
              {sending ? "Submitting…" : "Submit Application"} <Send size={14} />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Input({ icon: Icon, label, value, onChange, testid, required, type = "text", placeholder = "" }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-[#0F172A] mb-2">
        {label}{required && <span className="text-[#FF9933]"> *</span>}
      </span>
      <div className="relative">
        <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <input
          type={type} value={value} onChange={(e) => onChange(e.target.value)}
          required={required} placeholder={placeholder}
          data-testid={testid}
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm text-[#0F172A]"
        />
      </div>
    </label>
  );
}

function Select({ icon: Icon, label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-[#0F172A] mb-2">{label}</span>
      <div className="relative">
        <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <select
          value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-blue-200 focus:border-[#2563EB] focus:ring-4 focus:ring-blue-100 outline-none text-sm text-[#0F172A] bg-white"
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </label>
  );
}
